/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { useEffect, useState, useRef, lazy, Suspense } from "react";
import { useDispatch, useSelector } from "react-redux";
import { HiOutlineRefresh } from "react-icons/hi";
import t from "../../../Common/utils/translation/translator";
import "./Mechanics.scss";
import LoadsParameters from "../../components/LoadsParameters/LoadsParameters";
import Seperator from "../../components/Seperator/Seperator";
import CarriageQuantitySection from "../../components/Carriage/CarriageQuantitySection";
import CarriageOrientation from "../../components/Carriage/CarriageOrientation";
import CarriageParametersSection from "../../components/Carriage/CarriageParametersSection";
import ForcesParameters from "../../components/ForcesParameters/ForcesParameters";
import MechanicsSummary from "../../components/MechanicsSummary/MechanicsSummary";
import Navigator from "../../../Common/layouts/Navigator/Navigator";

import {
  resetAxesToCompare,
  selectCartesianReadOnlyMode,
  selectCartesianSteps,
  selectDynamics,
  selectMechanics,
  setAxisOptions,
  setAxisSelection,
  setCartesianCurrentStep,
  setDynamics,
  setMechanics,
  setMechanicsHash
} from "../../store/slices/cartesian";
import { updateConfiguration } from "../../../Common/store/slices/common";
import SectionHeader from "../../components/SectionHeader";
import CustomPopup from "../../../Common/components/CustomPopup/CustomPopup";
import MsxIcons from "../../../Common/components/Icons/MsxIcons/MsxIcons";
import { getAnimation3DTitle, validateForceParamRow } from "../../../Common/utils/api/common";
import {
  DEFAULT_AXIS_OPTIONS,
  DEFAULT_AXIS_SELECTION,
  getMaxOrientationAngle,
  getMechanicsHash,
  getMinOrientationAngle
} from "../../store/data/cartesianData";
import Spinner from "../../../Common/components/Icons/Spinner/Spinner";
import { setConfiguration, setFetchedBundles } from "../../../Retrofit/store/slices/retrofit";
const Animation3D = lazy(() => import("./Animation3D"));

const MainParamsection = ({ ...props }) => (
  <SectionHeader title={t("cartesian.ui.mechanics.main_parameters")} {...props} />
);

const Stickysection = ({ ...props }) => {
  const [animation3DFullScreen, setAnimation3DFullScreen] = useState(false);
  const [reset, setReset] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(1);

  const ref = useRef();
  useEffect(() => {
    const handleResize = () => {
      // Calculate the current zoom level
      const currentZoom = window.innerWidth / window.outerWidth;
      setZoomLevel(currentZoom);
      setReset(!reset);
    };

    // Listen for resize events
    window.addEventListener("resize", handleResize);

    // Initial call to set the zoom level
    handleResize();

    // Clean up event listener
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    const checkIfClickedOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setAnimation3DFullScreen(false);
        setReset(!reset);
      }
    };
    const handleEsc = (event) => {
      if (event.keyCode === 27) {
        setAnimation3DFullScreen(false);
        setReset(!reset);
      }
    };
    window.addEventListener("keydown", handleEsc);
    document.addEventListener("mousedown", checkIfClickedOutside);
    return () => {
      window.removeEventListener("keydown", handleEsc);
      document.removeEventListener("mousedown", checkIfClickedOutside);
    };
  }, []);

  const mechanics = useSelector(selectMechanics);
  const zoomPercentage = 100 / zoomLevel;
  var percentageMarginLeft = zoomLevel > 1 ? (100 - zoomPercentage) / 100 : 0;
  if (zoomPercentage < 50) {
    percentageMarginLeft = percentageMarginLeft + 1;
  }

  return (
    <div
      className="Stickysection bg-white rounded-sm fixed flex flex-col mt-20 w-[47%] responsive-height shadow-lg border-2"
      style={{ marginLeft: `${50 + percentageMarginLeft}%` }}
      {...props}
    >
      <div
        onClick={() => {
          setAnimation3DFullScreen(!animation3DFullScreen);
        }}
        className="absolute right-1 top-1 text-secondary m-1 text-base cursor-pointer z-10"
      >
        <MsxIcons name="expand" />
      </div>
      <div
        onClick={() => {
          setReset(!reset);
        }}
        className="absolute right-1 top-1 text-secondary mr-10 text-lg cursor-pointer z-10"
      >
        <HiOutlineRefresh className={false && "animate-spin"} />
        {t("cartesian.ui.mechanics.reset")}
      </div>
      <div className="text-sm text-black text-center">{getAnimation3DTitle(mechanics.selectedField)}</div>
      <div className="flex justify-center items-center content-center mb-2 flex-1">
        <Suspense fallback={<Spinner />}>
          <Animation3D key={animation3DFullScreen} zoomLevel={zoomLevel} reset={reset} />
        </Suspense>
      </div>
      <CustomPopup
        opened={animation3DFullScreen}
        wideWidth
        headerTitle={getAnimation3DTitle(mechanics.selectedField) || "Full Screen"}
        highlightHeaderTitle
        myref={ref}
      >
        <div className="h-[65vh] grid">
          <div
            onClick={() => {
              setReset(!reset);
            }}
            className="absolute right-1 top-1 text-white mr-10 text-lg cursor-pointer z-10"
          >
            <HiOutlineRefresh className={false && "animate-spin"} />
            {t("cartesian.ui.mechanics.reset")}
          </div>
          <Animation3D key={animation3DFullScreen} hideTitle zoomLevel={zoomLevel} reset={reset} />
          <div className="flex flex-col items-end justify-end">
            <qds-button
              onClick={() => {
                setAnimation3DFullScreen(false);
              }}
              text={t("cartesian.ui.mechanics.close")}
            ></qds-button>
          </div>
        </div>
      </CustomPopup>
    </div>
  );
};

const Mechanicssection = ({ ...props }) => {
  const dispatch = useDispatch();
  const steps = useSelector(selectCartesianSteps);
  const mechanics = useSelector(selectMechanics);
  const dynamics = useSelector(selectDynamics);
  const isReadOnlyMode = useSelector(selectCartesianReadOnlyMode);

  const validateAxisOrientation = () =>
    mechanics?.carriageOrientation !== undefined ? "" : t("cartesian.ui.mechanics.carriage_orientation");

  const validateStrokeLength = () => {
    return mechanics?.strokeLength !== undefined &&
      mechanics?.strokeLength !== "" &&
      Number(mechanics?.strokeLength) >= 7 &&
      Number(mechanics?.strokeLength) <= 5900 &&
      Number.isInteger(Number(mechanics?.strokeLength))
      ? ""
      : t("cartesian.ui.mechanics.stroke_length");
  };

  const validateAxisAngle = () => {
    const maxAngle = getMaxOrientationAngle(mechanics?.carriageOrientation);
    const minAngle = getMinOrientationAngle(mechanics?.carriageOrientation);
    return mechanics?.axisAngle !== undefined &&
      mechanics?.axisAngle !== "" &&
      mechanics?.axisAngle >= minAngle &&
      mechanics?.axisAngle <= maxAngle
      ? ""
      : t("cartesian.ui.mechanics.axis_angle");
  };

  const validateAxisSupport = () =>
    mechanics?.axisSupport !== undefined ? "" : t("cartesian.ui.mechanics.axis_support");

  const validateCarriageQuantity = () =>
    mechanics?.quantityCarriages !== undefined && mechanics?.quantityCarriages >= 1 && mechanics?.quantityCarriages <= 3
      ? ""
      : "Carriage quantity";

  const validateDistanceBetweenCarriages = () =>
    mechanics?.quantityCarriages <= 1 ||
    (mechanics?.distanceBetweenCarriages !== undefined &&
      mechanics?.distanceBetweenCarriages >= 35 &&
      mechanics?.distanceBetweenCarriages <= 999 &&
      mechanics?.distanceBetweenCarriages % 1 === 0)
      ? ""
      : t("cartesian.ui.mechanics.distance_between_carriages");

  const validateLoadsParameters = () =>
    mechanics?.loadsParameters?.length > 0 &&
    mechanics?.loadsParameters?.every(
      (loadParameter) =>
        loadParameter?.title !== undefined &&
        loadParameter?.title !== "" &&
        loadParameter?.payload !== undefined &&
        loadParameter?.payload !== "" &&
        Number(loadParameter?.payload) > 0 &&
        Number.isInteger(Number(loadParameter?.payload)) &&
        loadParameter?.dx !== "" &&
        loadParameter?.dy !== "" &&
        loadParameter?.dz !== ""
    )
      ? ""
      : t("cartesian.ui.mechanics.load_parameters");

  const validateForcesParameters = () =>
    (mechanics?.forcesParameters?.every(
      (forceParameter) =>
        !validateForceParamRow(
          forceParameter.title,
          forceParameter.fx,
          forceParameter.fy,
          forceParameter.fz,
          forceParameter.dx,
          forceParameter.dy,
          forceParameter.dz
        )
    ) ?? true)
      ? ""
      : t("cartesian.ui.mechanics.force_parameters");

  useEffect(() => {
    const validationResults = [
      validateAxisOrientation(),
      validateStrokeLength(),
      validateAxisAngle(),
      validateAxisSupport(),
      validateCarriageQuantity(),
      validateDistanceBetweenCarriages(),
      validateLoadsParameters(),
      validateForcesParameters()
    ];

    const missingFields = validationResults.filter(Boolean);

    const isValid = missingFields.length === 0;

    if (!isReadOnlyMode && isValid !== mechanics?.isValid) {
      console.log("Mechanics isValid", isValid);
      dispatch(setMechanics({ ...mechanics, isValid }));
    }
  }, [mechanics]);

  const handleContinue = () => {
    if (mechanics?.isValid) {
      !dynamics.isValid
        ? dispatch(
            setDynamics({
              ...dynamics,
              steps: dynamics.steps.map((step) => ({
                ...step,
                movement: mechanics.strokeLength
              }))
            })
          )
        : dispatch(
            setDynamics({
              ...dynamics,
              steps: dynamics.steps.map((step) => ({
                ...step,
                isValid: undefined
              }))
            })
          );
      dispatch(setCartesianCurrentStep(steps.DYNAMICS));
      dispatch(updateConfiguration());
      const mechanicsHash = getMechanicsHash(mechanics);
      //Reset dynamics and Axis Selection if hash is different
      if (mechanicsHash !== mechanics.hash) {
        //dispatch(setDynamics(DEFAULT_DYNAMICS));
        dispatch(setAxisSelection(DEFAULT_AXIS_SELECTION));
        dispatch(resetAxesToCompare());
        dispatch(setAxisOptions(DEFAULT_AXIS_OPTIONS));
        dispatch(setMechanicsHash(mechanicsHash));
        dispatch(setFetchedBundles([]));
        dispatch(setConfiguration(null));
      }
    }
  };
  const handlePrevious = () => {
    dispatch(setCartesianCurrentStep(steps.KINEMATICS));
    dispatch(updateConfiguration());
  };

  return (
    <div className="Mechanicssection w-[52%] mt-20 flex flex-col" {...props}>
      <MainParamsection data-testid="main-paramsection" />
      <CarriageOrientation data-testid="carriage-orientation" />
      <Seperator />
      <CarriageParametersSection
        data-testid="carriage-parameter-section"
        validateStrokeLength={validateStrokeLength() !== ""}
      />
      <Seperator />
      {/*MSC-1243 <AxisSection data-testid="axis-section" /> */}
      <Seperator />
      <CarriageQuantitySection data-testid="carriage-quantity-section" />
      <LoadsParameters data-testid="loads-parameters" />
      <ForcesParameters data-testid="forces-parameters" />

      <Navigator
        continueLabel={t("msizer.ui.footer.continue")}
        continueAction={() => {
          handleContinue();
        }}
        continueButtonDisabled={!mechanics?.isValid}
        previousAction={() => {
          handlePrevious();
        }}
      />
    </div>
  );
};

const Mechanics = ({ ...props }) => (
  <div className="mechanics" {...props}>
    <MechanicsSummary data-testid="mechanics-summary" />
    <div className="flex">
      <Mechanicssection data-testid="mechanics-section" />
      <Stickysection data-testid="sticky-section" />
    </div>
  </div>
);

export default Mechanics;
